import { useState } from 'react';
import { Alert, Box, CardContent, CardMedia, Modal, Skeleton, Stack, useTheme } from '@mui/material';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionWorkflowAi } from '@/ai/types/action-workflow-ai';
import { FileFormat, MediaLibStatus } from '@/graphql/generated/types.generated';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import _ from 'lodash';
import AndieGenericToolRender from './AndieGenericToolRender';
import { useTranslation } from 'react-i18next';
import { ImageRounded, InfoRounded } from '@mui/icons-material';

type ActionResponse = ActionWorkflowAi<{
    media: {
        name: string;
        path: string;
        type: FileFormat | null;
        status: MediaLibStatus;
        id: string;
        preview: string;
        specs: {
            width?: number | null | undefined;
            height?: number | null | undefined;
            duration?: number | null | undefined;
            fileSizeBytes?: number | null | undefined;
            fps?: number | null | undefined;
        } | null;
        mediaVariableUuid?: string | undefined;
    }[];
}>;
type ActionArgs = {
    network: string;
    ratio?: '1:1' | '3:2' | '2:3';
    numberOfImages: number;
    userInput?: string;
    prompt?: string;
    userPrompt?: string;
    useBranding?: boolean;
    profileImageReference?: 'auto' | 'include' | 'exclude';
    referenceMediaLibIds?: string[];
    privateProfileId?: string;
};

export type AndieBriefingToImageWorkflowProps = ActionRenderAi<ActionResponse, ActionArgs>;

const PREVIEW_WIDTH = 220;

export const AndieBriefingToImageWorkflow = (props: AndieBriefingToImageWorkflowProps) => {
    const { t } = useTranslation();
    const args = props.args;
    const isEdit = Boolean(args?.referenceMediaLibIds?.length);
    return (
        <AndieGenericToolRender<ActionResponse, ActionArgs>
            title={() =>
                isEdit
                    ? t('andie.tool.briefing-to-image.title-edit')
                    : t('andie.tool.briefing-to-image.title', { value: args?.numberOfImages || 1 })
            }
            icon={<ImageRounded sx={{ fontSize: 16 }} />}
            network={args?.network}
            loadingMessages={() => [
                isEdit
                    ? t('andie.tool.briefing-to-image.loading-edit')
                    : t('andie.tool.briefing-to-image.loading', { value: args?.numberOfImages || 1 }),
            ]}
            completeMessage={() =>
                isEdit
                    ? t('andie.tool.briefing-to-image.complete-edit')
                    : t('andie.tool.briefing-to-image.complete', { value: args?.numberOfImages || 1 })
            }
            additionalComponent={(props) => {
                const isComplete = props.status === ActionResponseStatusAi.COMPLETE;
                const skeletonRows = props.args?.numberOfImages || 1;
                const imageRatio = props.args?.ratio?.replace(':', ' / ') || '1 / 1';
                return (
                    <CardContent>
                        {!isComplete && (
                            <Alert icon={<InfoRounded fontSize="inherit" />} severity="info" sx={{ mb: 2 }}>
                                {t('andie.tool.briefing-to-image.alert')}
                            </Alert>
                        )}
                        {isComplete ? (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {(props.result?.result?.media || [])
                                    .filter((item) => item.preview || item.path)
                                    .map((item) => (
                                        <AndieBriefingToImageWorkflowPreview
                                            alt={item.name}
                                            src={item.preview || item.path}
                                            key={item.id}
                                        />
                                    ))}
                            </Stack>
                        ) : (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {_.times(skeletonRows, (i) => (
                                    <Skeleton
                                        key={i}
                                        variant="rounded"
                                        sx={{
                                            width: PREVIEW_WIDTH,
                                            maxWidth: '100%',
                                            aspectRatio: imageRatio,
                                            height: 'auto',
                                        }}
                                    />
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                );
            }}
            {...props}
        />
    );
};

const AndieBriefingToImageWorkflowPreview = ({
    key,
    src,
    alt,
}: {
    key: string;
    src: string;
    alt: string;
}) => {
    const theme = useTheme();
    const [previewModal, setPreviewModal] = useState(false);

    return (
        <>
            <Box
                key={`thumb-${key}`}
                sx={{ width: PREVIEW_WIDTH, maxWidth: '100%', cursor: 'pointer', position: 'relative' }}
            >
                <img
                    src={src}
                    alt={alt}
                    onClick={() => setPreviewModal(true)}
                    style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8 }}
                />
            </Box>
            <Modal
                open={previewModal}
                key={`modal-${key}`}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                onClose={() => setPreviewModal(false)}
            >
                <CardMedia
                    component={'img'}
                    src={src}
                    sx={{
                        maxHeight: '90%',
                        maxWidth: '90%',
                        width: 'auto',
                        height: 'auto',
                        backgroundColor: theme.palette.text.disabled,
                    }}
                />
            </Modal>
        </>
    );
};

export default AndieBriefingToImageWorkflow;
